import { Component, inject } from "@angular/core";
import { NgClass } from "@angular/common";
import { AbstractEffTestingStatusComponent } from "../abstract-eff-testing-status-component";
import { EffectivenessStatusType, SampleScore, TestStepScore } from "../../../shared/enums";
import { EffTableConfig } from "../../common/eff-table/models/eff-table-config.model";
import { EffTableColumnConfig } from "../../common/eff-table/models/eff-table-column-config.model";
import { EvidenceSampleTestStep } from "../models/evidence-sample-step.model";
import { FinalConclusion } from "../components/eff-final-conclusion-box/models/final-conclusion.model";
import { provideTranslocoScope, TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { EffStepHeaderComponent } from "../../common/eff-step-header/eff-step-header.component";
import { AuthorMessageBoxComponent } from "../components/author-message-box/author-message-box.component";
import { EffCheckboxComponent } from "../../common/eff-checkbox/eff-checkbox.component";
import { EffInlineTextEditorComponent } from "../../common/eff-inline-text-editor/eff-inline-text-editor.component";
import { DateFormatPipe } from "@app/shared/pipes/date-format.pipe";
import { DateTimeFormatPipe } from "@app/shared/pipes/date-time-format.pipe";
import { EffTableComponent, EffTableColumnDirective } from "../../common/eff-table/eff-table.component";
import { EffTextareaComponent } from "../../common/eff-textarea/eff-textarea.component";
import { DocumentsListComponent } from "../components/documents-list/documents-list.component";
import { EffIconComponent } from "../../common/eff-icon/eff-icon.component";
import { EffDetailFinalConclusionComponent } from "../../eff-detail-final-conclusion/eff-detail-final-conclusion.component";
import { EffButtonComponent } from "../../common/eff-button/eff-button.component";
import { EffTestInstructionsInfoBoxComponent } from "../../info-boxes/eff-test-instructions-info-box/eff-test-instructions-info-box.component";
import { EffEvidenceInstructionsInfoBoxComponent } from "../../info-boxes/eff-evidence-instructions-info-box/eff-evidence-instructions-info-box.component";
import { EffSourceDocumentInfoBoxComponent } from "../../info-boxes/eff-source-document-info-box/eff-source-document-info-box.component";
import { EffControlInfoBoxComponent } from "../../info-boxes/eff-control-info-box/eff-control-info-box.component";
import { CerrixSlideOverComponent } from "@app/shared/cerrix-slide-over/cerrix-slide-over.component";

@Component({
    selector: "eff-testing-review",
    templateUrl: "./eff-testing-review.component.html",
    styleUrls: ["./eff-testing-review.component.scss"],
    providers: [provideTranslocoScope("controls")],
    imports: [
        TranslocoPipe,
        NgClass,
        EffStepHeaderComponent,
        AuthorMessageBoxComponent,
        EffCheckboxComponent,
        EffInlineTextEditorComponent,
        DateFormatPipe,
        DateTimeFormatPipe,
        EffTableComponent,
        EffTableColumnDirective,
        EffTextareaComponent,
        DocumentsListComponent,
        EffIconComponent,
        EffDetailFinalConclusionComponent,
        EffButtonComponent,
        EffTestInstructionsInfoBoxComponent,
        EffEvidenceInstructionsInfoBoxComponent,
        EffSourceDocumentInfoBoxComponent,
        EffControlInfoBoxComponent,
        CerrixSlideOverComponent,
    ]
})
export class EffTestingReviewComponent extends AbstractEffTestingStatusComponent {
    private _transloco = inject(TranslocoService);
    protected loadSamples: boolean = true;
    protected hideSampling = false;

    isNonOccurrenceTestPlan = false;

    tableConfig: EffTableConfig = {
        ExpandableRow: true,
        SeparateRows: true,
        RowClassFunc(item: EvidenceSampleTestStep) {
            if (item.isSample) {
                return "sample-row";
            }
            return "";
        },
    };

    displayedColumns: Array<EffTableColumnConfig> = [
        { PropertyName: "name", DisplayedNameKey: "controls.effGrid.columnName", IsTemplate: true, Width: "25%" },
        {
            PropertyName: "scoreTester",
            DisplayedNameKey: "controls.effGrid.columnScoreTester",
            IsTemplate: true,
            Width: "25%",
        },
        {
            PropertyName: "testerComment",
            DisplayedNameKey: "controls.effGrid.columnCommentTester",
            IsTemplate: true,
            Width: "25%",
        },
        {
            PropertyName: "reviewerComment",
            DisplayedNameKey: "controls.effGrid.columnCommentReviewer",
            IsTemplate: true,
            Width: "25%",
        },
    ];

    override onLoad() {
        this.isNonOccurrenceTestPlan =
            this.effectivenessModel.status == EffectivenessStatusType.reviewNonOccurrence;
    }

    getScoreColor(item: EvidenceSampleTestStep) {
        if (!item.isSample) {
            switch (item.stepScore) {
                case TestStepScore.correct:
                    return "score-correct";
                case TestStepScore.inapplicable:
                    return "score-inapplicable";
                case TestStepScore.incorrect:
                    return "score-incorrect";
                case TestStepScore.missingEvidence:
                    return "score-missing-evidence";
                case TestStepScore.noScore:
                    return "no-score";
            }
        } else {
            switch (item.sampleScore) {
                case SampleScore.sufficient:
                    return "score-correct";
                case SampleScore.inapplicable:
                    return "score-inapplicable";
                case SampleScore.insufficient:
                    return "score-incorrect";
                case SampleScore.noScore:
                    return "no-score";
            }
        }
    }

    async accept() {
        this.giveFinalConclusion(
            this.getWorkflowPromptTitle(this._transloco.translate("controls.effTestingReview.accept")),
            this._transloco.translate("controls.effTestingReview.accept"),
            this._transloco.translate("controls.effTestingReview.acceptMessage"),
            (async (finalConclusion: FinalConclusion) => {
                await this.saveChanges(false);

                const call = this.isNonOccurrenceTestPlan
                    ? this._workflowDs.postWorkflowNonOccurrenceAcceptReviewer(
                          this.effectivenessModel.guid,
                          {
                              comment: finalConclusion.conclusion,
                              scoreId: finalConclusion.score,
                          }
                      )
                    : this._workflowDs.postWorkflowAcceptReviewer(this.effectivenessModel.guid, {
                          comment: finalConclusion.conclusion,
                          scoreId: finalConclusion.score,
                      });

                call.subscribe(() => {
                    this.cerrixTab.refresh();
                });
            }).bind(this),
            "600px",
            true
        );
    }

    async modify() {
        this.giveFinalConclusion(
            this.getWorkflowPromptTitle(this._transloco.translate("controls.effTestingReview.acceptWithModification")),
            this._transloco.translate("controls.effTestingReview.modify"),
            this._transloco.translate("controls.effTestingReview.modifyMessage"),
            (async (finalConclusion: FinalConclusion) => {
                await this.saveChanges(false);

                const call = this.isNonOccurrenceTestPlan
                    ? this._workflowDs.postWorkflowNonOccurrenceModifyReviewer(
                          this.effectivenessModel.guid,
                          {
                              comment: finalConclusion.conclusion,
                              scoreId: finalConclusion.score,
                          }
                      )
                    : this._workflowDs.postWorkflowModifyReviewer(this.effectivenessModel.guid, {
                          comment: finalConclusion.conclusion,
                          scoreId: finalConclusion.score,
                      });

                call.subscribe(() => {
                    this.cerrixTab.refresh();
                });
            }).bind(this)
        );
    }

    async reject() {
        this.giveFinalConclusion(
            this.getWorkflowPromptTitle(this._transloco.translate("controls.effTestingReview.reject")),
            this._transloco.translate("controls.effTestingReview.reject"),
            this._transloco.translate("controls.effTestingReview.rejectMessage"),
            (async (finalConclusion: FinalConclusion) => {
                await this.saveChanges(false);

                const call = this.isNonOccurrenceTestPlan
                    ? this._workflowDs.postWorkflowNonOccurrenceRejectReviewer(
                          this.effectivenessModel.guid,
                          {
                              comment: finalConclusion.conclusion,
                              scoreId: finalConclusion.score,
                          }
                      )
                    : this._workflowDs.postWorkflowRejectReviewer(this.effectivenessModel.guid, {
                          comment: finalConclusion.conclusion,
                          scoreId: finalConclusion.score,
                      });

                call.subscribe(() => {
                    this.cerrixTab.refresh();
                });
            }).bind(this),
            "600px",
            true
        );
    }

    private getWorkflowPromptTitle(action: string): string {
        return action + (this.isNonOccurrenceTestPlan ? this._transloco.translate("controls.effTestingReview.nonOccurrenceSuffix") : "");
    }

    async saveChanges(doRefresh: boolean) {
        await this.saveSampleChanges();
        if (doRefresh) {
            this.cerrixTab.refresh();
        }
    }
}
